import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, Zap, Globe, ArrowRight, CheckCircle } from 'lucide-react';

const reasons = [
  { title: 'Maximum Security', icon: Shield, desc: 'Every shipment is fully insured and tracked with military-grade GPS precision. Zero damage record across 50,000+ deliveries.', color: '#FF6B00' },
  { title: 'Precision Timing',  icon: Clock,  desc: '99.8% on-time delivery rate across all major industrial and commercial routes. We treat your deadlines as our own.', color: '#3b82f6' },
  { title: 'Express Dispatch',  icon: Zap,    desc: 'Instant vehicle assignment within minutes of booking. Rapid loading turnaround and same-day dispatch available.', color: '#f59e0b' },
  { title: 'Pan-India Reach',   icon: Globe,  desc: 'Connecting 250+ cities with a premium fleet network. From metro hubs to tier-2 industrial corridors.', color: '#22c55e' },
];

const highlights = [
  'ISO Certified Operations',
  'Real-Time GPS Tracking',
  'Dedicated Account Manager',
  '24/7 Customer Support',
  'Transit Insurance Included',
  'Digital Proof of Delivery',
];

const WhyChooseUs = () => (
  <section className="section-padding bg-brand-bg relative overflow-hidden">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
      className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] border border-brand-orange/5 rounded-full pointer-events-none"
    />
    <motion.div
      animate={{ rotate: -360 }}
      transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
      className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[400px] h-[280px] sm:h-[400px] border border-brand-orange/8 rounded-full pointer-events-none"
    />

    <div className="max-w-7xl mx-auto relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-20 items-center">

        {/* Left */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 60 }}
          className="space-y-6 sm:space-y-8"
        >
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ scaleX: [0, 1] }}
              transition={{ duration: 0.8 }}
              className="h-[2px] w-12 bg-brand-orange"
            />
            <span className="text-brand-orange font-black tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-xs uppercase">The PkRouteX Edge</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight text-brand-black">
            WHY THE BEST <br />
            <span className="text-brand-orange">CHOOSE US.</span>
          </h2>

          <p className="text-brand-muted text-base sm:text-lg leading-relaxed">
            We don't just move cargo — we move your business forward. Our integrated technology platform, premium fleet, and dedicated operations team ensure your logistics are as reliable as your vision.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="flex items-center gap-2"
              >
                <CheckCircle size={15} className="text-brand-orange shrink-0" />
                <span className="text-brand-muted text-sm font-medium">{h}</span>
              </motion.div>
            ))}
          </div>

          <motion.a
            href="#book"
            onClick={(e) => { e.preventDefault(); document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' }); }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-3 bg-brand-black text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-black hover:bg-brand-orange transition-all duration-300 group text-sm"
          >
            GET STARTED
            <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>
              <ArrowRight size={18} />
            </motion.span>
          </motion.a>
        </motion.div>

        {/* Right */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 relative">
          <div className="absolute -inset-4 bg-brand-orange/6 blur-3xl rounded-full -z-10" />

          {reasons.map((reason, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="bg-white p-5 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] border border-black/5 shadow-sm hover:border-brand-orange/30 hover:shadow-xl transition-all group cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 -left-full w-2/3 h-full bg-gradient-to-r from-transparent via-brand-orange/5 to-transparent skew-x-[-20deg] group-hover:left-[150%] transition-all duration-700 pointer-events-none" />

              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  className="w-10 sm:w-12 h-10 sm:h-12 rounded-xl flex items-center justify-center transition-all duration-300"
                  style={{ background: `${reason.color}15` }}
                >
                  <reason.icon size={22} style={{ color: reason.color }} />
                </motion.div>
                <span className="font-black text-3xl sm:text-4xl" style={{ color: `${reason.color}20` }}>
                  0{idx + 1}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-black mb-2 text-brand-black group-hover:text-brand-orange transition-colors duration-300">{reason.title}</h3>
              <p className="text-brand-muted text-xs sm:text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}

          {/* Warehouse image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="sm:col-span-2 mt-2 rounded-2xl sm:rounded-3xl overflow-hidden h-44 sm:h-56 relative group"
          >
            <motion.img
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.6 }}
              src="/images/warehouse.png"
              alt="PKROUTEX Advanced Warehousing and Logistics Infrastructure"
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 sm:bottom-5 left-4 sm:left-6">
              <p className="text-brand-orange font-black text-[10px] sm:text-xs uppercase tracking-widest mb-1">Infrastructure</p>
              <h4 className="text-brand-black font-black text-base sm:text-lg">Advanced Warehousing Solutions</h4>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

export default WhyChooseUs;
